import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { API_CUSTOMER_CONTACT } from '@/constants/urls'
import { axiosApi } from '@/services/http/axiosApi'
import { logger } from '@/services/logger'
import { now, SERVER_FORMAT } from '@/helpers/date-helpers'
import { i18nService } from '@accor/customer-vue-services'
import {
  FOOD_QUESTIONS,
  INTEREST_QUESTIONS,
  REGIME_QUESTIONS,
  ROOM_QUESTIONS,
  type PreferenceReferential,
  type PreferenceReferentialValue,
  type PreferencesResponse,
  type PreferencesValue,
  type QuestionPreferenceType,
  type UserPreferenceValue,
  type PutPreferenceResponse,
  RECOMMENDATION_QUESTIONS
} from '@/constants/preferences'

export const usePreferencesStore = defineStore('preferences', () => {
  const userPreferences = ref<PreferencesValue[]>([])
  const preferencesReferential = ref<PreferenceReferential[]>([])

  const referentialByValueCode = computed(() =>
    preferencesReferential.value.reduce(
      (acc, pref) => {
        pref.values.forEach((prefValue) => {
          acc[prefValue.valueCode] = {
            preference: pref,
            value: prefValue
          }
        })
        return acc
      },
      {} as Record<string, { preference: PreferenceReferential; value: PreferenceReferentialValue }>
    )
  )

  const userPreferencesByFamily = computed(() => {
    const families: Record<string, UserPreferenceValue[]> = {}

    userPreferences.value.forEach((preferenceValue) => {
      const preferenceReferentialValue = referentialByValueCode.value[preferenceValue.code]?.value

      if (preferenceReferentialValue) {
        families[preferenceReferentialValue.familyCode] ||= []
        families[preferenceReferentialValue.familyCode].push({
          ...preferenceReferentialValue,
          id: preferenceValue.id
        })
      }
    })

    return families
  })

  const filterChipsForQuestions = (choicePreferences: QuestionPreferenceType, limit = 8) => {
    const userPrefs = userPreferences.value.map(({ code }) => code)

    const filteredChoices = choicePreferences.filter((choices) =>
      Array.isArray(choices)
        ? !choices.some((choice) => userPrefs?.includes(choice))
        : !userPrefs.includes(choices)
    )

    const limitedChoices = limit ? filteredChoices.slice(0, limit) : filteredChoices

    return limitedChoices.map((choices) => {
      const preferenceCode = Array.isArray(choices) ? choices[0] : choices
      const preference = referentialByValueCode.value[preferenceCode]?.preference

      return {
        ...preference,
        values: preference?.values?.filter(({ valueCode }) =>
          Array.isArray(choices) ? choices.includes(valueCode) : choices === valueCode
        )
      }
    })
  }

  const roomChoicePreferences = computed(() => filterChipsForQuestions(ROOM_QUESTIONS))
  const foodChoicePreferences = computed(() => filterChipsForQuestions(FOOD_QUESTIONS))
  const regimeChoicePreferences = computed(() => filterChipsForQuestions(REGIME_QUESTIONS))
  const interestChoicePreferences = computed(() => filterChipsForQuestions(INTEREST_QUESTIONS))
  const availableChoicePreferences = computed(() =>
    filterChipsForQuestions(RECOMMENDATION_QUESTIONS, 0)
  )

  const loadPreferencesReferential = async () => {
    const locale = i18nService.getCurrentLocale()
    preferencesReferential.value = (await import(`../assets/preferences/${locale}.json`)).default
  }

  const loadUserPreferences = async () => {
    try {
      const { data } = await axiosApi.get<PreferencesResponse>(API_CUSTOMER_CONTACT, {
        params: {
          fields:
            'individual.individualPreferenceFamilies.individualPreferenceFamily.individualPreferences.individualPreference.preferenceValues.preferenceValue[id,code,comment,favoriteClient]'
        }
      })

      userPreferences.value =
        data?.individual?.individualPreferenceFamilies?.individualPreferenceFamily?.flatMap(
          (family) =>
            family?.individualPreferences?.individualPreference?.flatMap(
              (pref) => pref.preferenceValues.preferenceValue
            )
        ) || []
    } catch (err: any) {
      logger.error(err)
    }

    return userPreferences.value
  }

  const deleteUserPreference = async (id: string) => {
    await axiosApi.delete(`${API_CUSTOMER_CONTACT}/preferences/${id}`)
    await loadUserPreferences()
  }

  const putUserPreference = async (code: string) =>
    axiosApi.put<PutPreferenceResponse>(`${API_CUSTOMER_CONTACT}/preferences`, {
      favoriteClient: false,
      preferenceValueCode: code,
      integrationId: '',
      //Todo: optInDate is mandatory for sensitive prefs and optional for others, we always send it but we shouldn't for legal reasons
      optInDate: now().format(SERVER_FORMAT)
    })

  const flattenedPreferencesReferentialExcludingUserPrefForSearchBar = computed(() => {
    const flattenedPreferences = preferencesReferential.value
      .flatMap((preference) => preference.values)
      .map((value) => ({
        label: value.valueLabel,
        value: value.valueCode,
        data: value
      }))
    return flattenedPreferences.filter(
      (preference) => !userPreferences.value.some((userPref) => userPref.code === preference.value)
    )
  })

  return {
    referentialByValueCode,
    loadPreferencesReferential,
    preferencesReferential,
    flattenedPreferencesReferentialExcludingUserPrefForSearchBar,
    loadUserPreferences,
    deleteUserPreference,
    userPreferences,
    userPreferencesByFamily,
    roomChoicePreferences,
    foodChoicePreferences,
    regimeChoicePreferences,
    interestChoicePreferences,
    putUserPreference,
    filterChipsForQuestions,
    availableChoicePreferences
  }
})
